import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';

// Bot URL — same as Cloud Run
const BOT_URL = process.env.REACT_APP_BOT_URL || 'http://localhost:8080';

const getSafeUrl = (url) => {
  if (!url) return '#';
  const trimmed = url.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return '#';
};

// Excel template download now uses static file directly
function downloadTemplate() {
  const link = document.createElement('a');
  link.href = '/admin/Patna_Stores.xlsx';
  link.download = 'Patna_Stores.xlsx';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export default function ExcelImport() {
  const [preview, setPreview]     = useState(null); // parsed data
  const [status, setStatus]       = useState('idle'); // idle | parsing | confirm | applying | done | error
  const [result, setResult]       = useState(null);
  const [errorMsg, setErrorMsg]   = useState('');
  const [password, setPassword]   = useState(() => sessionStorage.getItem('eco_admin_pw') || '');
  const [cleanDatabase, setCleanDatabase] = useState(false);
  const fileRef = useRef();

  // ── Parse uploaded Excel ─────────────────────────────────────────────────
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setStatus('parsing');
    setErrorMsg('');
    setPreview(null);

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = new Uint8Array(ev.target.result);
        const wb = XLSX.read(data, { type: 'array' });

        if (wb.SheetNames.length === 0) {
          throw new Error("Excel file is empty");
        }

        const ws = wb.Sheets[wb.SheetNames[0]];
        const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

        // 1. Find the header row
        let headerRowIdx = -1;
        let colIndices = {};
        for (let i = 0; i < Math.min(15, aoa.length); i++) {
          const row = aoa[i].map(c => String(c).toLowerCase().replace(/[\s()_]/g, ''));
          if (row.includes('storename') && row.includes('outletid')) {
            headerRowIdx = i;
            colIndices = {
              srNo: aoa[i].findIndex(c => { const s = String(c).toLowerCase().replace(/[\s()_]/g, ''); return s === 'srno' || s === 'slno'; }),
              storeName: aoa[i].findIndex(c => String(c).toLowerCase().replace(/[\s()_]/g, '') === 'storename'),
              outletId: aoa[i].findIndex(c => String(c).toLowerCase().replace(/[\s()_]/g, '') === 'outletid'),
              location: aoa[i].findIndex(c => String(c).toLowerCase().replace(/[\s()_]/g, '') === 'location'),
              state: aoa[i].findIndex(c => String(c).toLowerCase().replace(/[\s()_]/g, '') === 'state'),
              city: aoa[i].findIndex(c => String(c).toLowerCase().replace(/[\s()_]/g, '') === 'city'),
              priority: aoa[i].findIndex(c => String(c).toLowerCase().replace(/[\s()_]/g, '') === 'priority'),
              address: aoa[i].findIndex(c => String(c).toLowerCase().replace(/[\s()_]/g, '') === 'address'),
              mapLocation: aoa[i].findIndex(c => { const s = String(c).toLowerCase().replace(/[\s()_]/g, ''); return s === 'maplocation' || s === 'maplink'; }),
              storeManagerContact: aoa[i].findIndex(c => { const s = String(c).toLowerCase().replace(/[\s()_]/g, ''); return s.includes('storemanagercontact') || s.includes('managercontact'); }),
              pickerName: aoa[i].findIndex(c => String(c).toLowerCase().replace(/[\s()_]/g, '') === 'pickername'),
              pickerPhone: aoa[i].findIndex(c => { const s = String(c).toLowerCase().replace(/[\s()_]/g, ''); return s.includes('pickerphone') || s.includes('phone'); }),
              active: aoa[i].findIndex(c => String(c).toLowerCase().replace(/[\s()_]/g, '') === 'active'),
            };
            break;
          }
        }

        if (headerRowIdx === -1) {
          // Fallback mapping
          headerRowIdx = 0;
          colIndices = {
            srNo: 0, storeName: 1, outletId: 2, location: 3, state: 4, city: 5,
            priority: 6, address: 7, mapLocation: 8, storeManagerContact: 9,
            pickerName: 10, pickerPhone: 11, active: 12
          };
        }

        const locations = [];
        const workersMap = {};
        const startIdx = headerRowIdx + 1;

        for (let i = startIdx; i < aoa.length; i++) {
          const row = aoa[i];
          if (!row || row.length === 0 || row.join('').trim() === '') continue;

          const storeName = String(row[colIndices.storeName] || '').trim();
          if (!storeName) continue;

          // Extract locId
          let locId = '';
          const locMatch = storeName.match(/(ES\s*\d+|LT\s*\d+)/i);
          if (locMatch) {
            locId = locMatch[0].replace(/\s+/g, '').toUpperCase();
          } else {
            locId = storeName.replace(/\s+/g, '').toUpperCase();
          }

          const isActiveVal = String(row[colIndices.active] || 'Yes').trim().toLowerCase();
          const isActive = isActiveVal !== 'no' && isActiveVal !== 'false' && isActiveVal !== 'inactive';

          locations.push({
            locationId: locId,
            locationName: storeName,
            outletId: String(row[colIndices.outletId] || '').trim(),
            location: String(row[colIndices.location] || '').trim(),
            state: String(row[colIndices.state] || '').trim(),
            city: String(row[colIndices.city] || '').trim(),
            priority: String(row[colIndices.priority] || '').trim(),
            address: String(row[colIndices.address] || '').trim(),
            mapLocation: String(row[colIndices.mapLocation] || '').trim(),
            storeManagerContact: String(row[colIndices.storeManagerContact] || '').trim(),
            active: isActive
          });

          const pickerName = String(row[colIndices.pickerName] || '').trim();
          const pickerPhone = String(row[colIndices.pickerPhone] || '').trim();

          if (pickerName && pickerName.toLowerCase() !== 'na' && pickerName.toLowerCase() !== 'n/a') {
            const names = pickerName.split(/[\/,|&]/).map(n => n.trim()).filter(Boolean);
            const phones = pickerPhone.split(/[\/,|&]/).map(p => p.trim()).filter(Boolean);
            const count = Math.max(names.length, phones.length);

            for (let k = 0; k < count; k++) {
              const pName = names[k] || names[0] || '';
              const pPhone = phones[k] || '';

              if (pName && pName.toLowerCase() !== 'na' && pName.toLowerCase() !== 'n/a') {
                const suffix = count > 1 ? String.fromCharCode(65 + k) : ''; // '', 'A', 'B', etc.
                const cleanPhone = pPhone.replace(/\D/g, '');
                const key = cleanPhone || pName;
                const srNo = row[colIndices.srNo] || i;
                const workerId = `ECO-${String(srNo).padStart(3, '0')}${suffix}`;

                if (!workersMap[key]) {
                  workersMap[key] = {
                    workerId,
                    name: pName,
                    phone: pPhone,
                    locations: [locId],
                    active: isActive
                  };
                } else {
                  if (!workersMap[key].locations.includes(locId)) {
                    workersMap[key].locations.push(locId);
                  }
                }
              }
            }
          }
        }

        const workers = Object.values(workersMap);

        if (locations.length === 0) {
          throw new Error("Locations sheet mein data nahi mila.");
        }

        setPreview({ workers, locations });
        setStatus('confirm');
      } catch (err) {
        setErrorMsg('❌ Excel parse nahi ho saka: ' + err.message);
        setStatus('error');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // ── Apply changes ────────────────────────────────────────────────────────
  const applyChanges = async () => {
    if (!password) { alert('Admin password enter karein'); return; }
    setStatus('applying');

    try {
      const res = await fetch(`${BOT_URL}/admin/update-workers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          adminPassword: password,
          workers:   preview.workers,
          locations: preview.locations,
          cleanDatabase: cleanDatabase
        }),
      });
      const data = await res.json();
      if (data.success) {
        setResult(data);
        setStatus('done');
      } else {
        if (res.status === 401) {
          sessionStorage.removeItem('eco_admin');
          sessionStorage.removeItem('eco_admin_pw');
          window.location.reload();
          return;
        }
        setErrorMsg('❌ ' + (data.error || 'Server error'));
        setStatus('error');
      }
    } catch (err) {
      setErrorMsg('❌ Network error: ' + err.message + '\n\nCloud Run deployed hai? URL correct hai?');
      setStatus('error');
    }
  };

  const reset = () => {
    setStatus('idle'); setPreview(null); setResult(null);
    setErrorMsg(''); setPassword(sessionStorage.getItem('eco_admin_pw') || ''); setCleanDatabase(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: 760 }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>
          📊 Excel se Update Karo
        </h2>
        <p style={{ color: '#64748b', fontSize: 14, lineHeight: 1.6 }}>
          Nayi Excel upload karo — workers, locations aur assignments update ho jaayenge.
          <strong style={{ color: '#94a3b8' }}> Purana attendance data bilkul safe rahega.</strong>
        </p>
      </div>

      {/* Download Template */}
      <div style={{
        background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.2)',
        borderRadius: 12, padding: '16px 20px', marginBottom: 24,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12
      }}>
        <div>
          <div style={{ fontWeight: 600, color: '#93c5fd', marginBottom: 4 }}>📥 Template Download Karo</div>
          <div style={{ color: '#64748b', fontSize: 13 }}>
            Pehle template download karo → fill karo → upload karo
          </div>
        </div>
        <button onClick={downloadTemplate} style={{
          background: 'rgba(96,165,250,0.15)', border: '1px solid rgba(96,165,250,0.3)',
          borderRadius: 8, padding: '8px 18px', color: '#93c5fd',
          fontSize: 13, fontWeight: 600, cursor: 'pointer'
        }}>
          📥 Template Download
        </button>
      </div>

      {/* ── IDLE STATE ── */}
      {(status === 'idle' || status === 'parsing' || status === 'error') && (
        <div>
          <label style={{
            display: 'block',
            background: 'rgba(255,255,255,0.03)', border: '2px dashed rgba(255,255,255,0.15)',
            borderRadius: 16, padding: '40px 24px', textAlign: 'center', cursor: 'pointer',
            transition: 'all 0.2s'
          }}>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleFile}
              style={{ display: 'none' }} />
            <div style={{ fontSize: 48, marginBottom: 12 }}>📊</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#e2e8f0', marginBottom: 6 }}>
              Excel File Upload Karo
            </div>
            <div style={{ color: '#64748b', fontSize: 13 }}>
              .xlsx ya .xls file — Workers + Locations sheets
            </div>
          </label>

          {status === 'parsing' && (
            <div style={{ textAlign: 'center', padding: 24, color: '#60a5fa' }}>
              ⏳ Parsing Excel...
            </div>
          )}
          {status === 'error' && errorMsg && (
            <div style={{
              background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)',
              borderRadius: 10, padding: '14px 16px', marginTop: 16,
              color: '#f87171', fontSize: 14, whiteSpace: 'pre-wrap'
            }}>{errorMsg}</div>
          )}
        </div>
      )}

      {/* ── CONFIRM STATE ── */}
      {status === 'confirm' && preview && (
        <div>
          {/* Preview: Workers */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 15 }}>
              👥 Workers Preview — {preview.workers.length} records
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.03)', borderRadius: 12,
              border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'rgba(255,255,255,0.06)' }}>
                    {['Worker ID', 'Name', 'Location(s)', 'Phone', 'Status'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: '#94a3b8', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.workers.slice(0, 10).map((w, i) => (
                    <tr key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                      <td style={{ padding: '9px 14px', color: '#60a5fa', fontWeight: 600 }}>{w.workerId}</td>
                      <td style={{ padding: '9px 14px', color: '#e2e8f0' }}>{w.name}</td>
                      <td style={{ padding: '9px 14px', color: '#34d399' }}>{w.locations.join(', ')}</td>
                      <td style={{ padding: '9px 14px', color: '#94a3b8' }}>{w.phone || '—'}</td>
                      <td style={{ padding: '9px 14px' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: 6, fontSize: 11,
                          background: w.active ? 'rgba(52,211,153,0.15)' : 'rgba(248,113,113,0.15)',
                          color: w.active ? '#34d399' : '#f87171'
                        }}>
                          {w.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.workers.length > 10 && (
                <div style={{ padding: '10px 14px', color: '#64748b', fontSize: 12, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                  ...aur {preview.workers.length - 10} workers
                </div>
              )}
            </div>
          </div>

          {/* Preview: Locations */}
          {preview.locations.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 15 }}>
                📍 Locations Data (Address, Maps, Priority) — {preview.locations.length} records
              </div>
              <div style={{
                background: 'rgba(255,255,255,0.03)', borderRadius: 12,
                border: '1px solid rgba(255,255,255,0.08)', overflowX: 'auto'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 800 }}>
                  <thead>
                    <tr style={{ background: 'rgba(255,255,255,0.06)' }}>
                      {['ID', 'Name', 'Priority', 'Map Link', 'Manager Contact'].map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: '#94a3b8', fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.locations.map((l, i) => (
                      <tr key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                        <td style={{ padding: '9px 14px', color: '#60a5fa', fontWeight: 600 }}>{l.locationId}</td>
                        <td style={{ padding: '9px 14px', color: '#e2e8f0' }}>{l.locationName}</td>
                        <td style={{ padding: '9px 14px', color: '#fbbf24', fontWeight: 600 }}>{l.priority || '—'}</td>
                        <td style={{ padding: '9px 14px' }}>
                          {l.mapLocation ? (
                            <a href={getSafeUrl(l.mapLocation)} target="_blank" rel="noreferrer" style={{ color: '#34d399', textDecoration: 'none' }}>
                              🗺️ View Map
                            </a>
                          ) : '—'}
                        </td>
                        <td style={{ padding: '9px 14px', color: '#94a3b8' }}>
                          {l.storeManagerContact || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Warning box */}
          <div style={{
            background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)',
            borderRadius: 10, padding: '14px 16px', marginBottom: 20,
          }}>
            <div style={{ color: '#fbbf24', fontWeight: 600, marginBottom: 6 }}>⚠️ Confirm Before Apply</div>
            <div style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.6 }}>
              • Workers ke location assignments aaj ki date se update honge<br/>
              • Naye workers add honge, existing workers update honge<br/>
              • Yeh action undo nahi hoti
            </div>
          </div>

          {/* Wipe DB Option */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ 
              display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
              padding: '14px 16px', background: 'rgba(239,68,68,0.08)', 
              border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10 
            }}>
              <input
                type="checkbox"
                checked={cleanDatabase}
                onChange={e => setCleanDatabase(e.target.checked)}
                style={{ width: 18, height: 18, cursor: 'pointer' }}
              />
              <span style={{ color: '#f87171', fontSize: 13, fontWeight: 600 }}>
                🗑️ Wipe current database (Delete all existing Workers, Locations, and Attendance records before importing)
              </span>
            </label>
          </div>

          {/* Password + Apply */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'stretch', flexWrap: 'wrap' }}>
            <input
              type="password"
              placeholder="Admin password confirm karein"
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={{
                flex: 1, minWidth: 200,
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 10, padding: '12px 16px', color: '#e2e8f0', fontSize: 14, outline: 'none'
              }}
            />
            <button onClick={applyChanges} style={{
              background: 'linear-gradient(135deg, #34d399, #059669)',
              border: 'none', borderRadius: 10, padding: '12px 24px',
              color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer'
            }}>
              ✅ Apply Changes
            </button>
            <button onClick={reset} style={{
              background: 'transparent', border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 10, padding: '12px 20px', color: '#94a3b8',
              fontSize: 14, cursor: 'pointer'
            }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── APPLYING STATE ── */}
      {status === 'applying' && (
        <div style={{ textAlign: 'center', padding: '40px 24px' }}>
          <div style={{
            width: 48, height: 48, border: '3px solid rgba(52,211,153,0.2)',
            borderTopColor: '#34d399', borderRadius: '50%',
            animation: 'spin 0.8s linear infinite', margin: '0 auto 16px'
          }}></div>
          <div style={{ color: '#94a3b8', fontSize: 15 }}>Firestore update ho raha hai...</div>
        </div>
      )}

      {/* ── DONE STATE ── */}
      {status === 'done' && result && (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
          <h3 style={{ fontSize: 20, fontWeight: 700, color: '#34d399', marginBottom: 8 }}>
            Update Successfully Applied!
          </h3>
          <p style={{ color: '#64748b', marginBottom: 24, fontSize: 14 }}>
            Changes effective from: <strong style={{ color: '#e2e8f0' }}>{result.effectiveFrom}</strong>
          </p>

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 24
          }}>
            {[
              { label: 'Workers Added',     value: result.added,            color: '#34d399' },
              { label: 'Workers Updated',   value: result.updated,          color: '#60a5fa' },
              { label: 'Locations Updated', value: result.locationsUpdated, color: '#a78bfa' },
              { label: 'Historical Data',   value: '✅ Safe',               color: '#fbbf24' },
            ].map(s => (
              <div key={s.label} style={{
                background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 12, padding: '16px', textAlign: 'left'
              }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>{s.label}</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>

          <button onClick={reset} style={{
            background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 10, padding: '10px 24px', color: '#94a3b8',
            fontSize: 14, cursor: 'pointer'
          }}>
            ↩ Another Update
          </button>
        </div>
      )}
    </div>
  );
}
